import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { useHandlers } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { useResetOnChange } from '@sparklayer-io/spark-dash-ui/hooks/use-reset-on-change';
import { Accordion, Button, LearnMoreLink, Text, TextLink } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { DOCS_URLS } from '../../constants';
import { OnboardingChecklist, OnboardingChecklistItem } from './onboarding-checklist';
import { onboardingStepBadge } from './onboarding-step-badge';
import { OnboardingStepIcon } from './onboarding-step-icon';

const COMPONENT = 'features:home.components.onboarding.sync' satisfies I18nPrefix;

export function SyncStep({
  isExpanded,
  onExpandedChange,
  loading,
  onToggle,
  initialPrecheck,
}: {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  loading: boolean;
  onToggle: () => void;
  /** Preticks both checklist items - the price-list assign flow deep-links here already done. */
  initialPrecheck: boolean;
}) {
  const env = useEnv();
  const { navigationHandler } = useHandlers();
  const complete = env.onboarding_sync;

  const [createdList, setCreatedList] = useState<boolean>(initialPrecheck);
  const [reviewedAdvanced, setReviewedAdvanced] = useState<boolean>(initialPrecheck);
  const allChecked = createdList && reviewedAdvanced;

  // Unchecking the step resets its checklist so the user re-acknowledges each item. Not run on
  // mount, so it doesn't wipe out `initialPrecheck` before it's seen.
  useResetOnChange([complete], () => {
    if (!complete) {
      setCreatedList(false);
      setReviewedAdvanced(false);
    }
  });

  return (
    <Accordion.Item
      title={t(`${COMPONENT}.title`)}
      subtitle={t(complete ? `${COMPONENT}.subtitle_complete` : `${COMPONENT}.subtitle_incomplete`)}
      icon={<OnboardingStepIcon complete={complete} />}
      badge={onboardingStepBadge(complete)}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    >
      <Text variant="secondary">
        {t(`${COMPONENT}.description`)}{' '}
        <TextLink onClick={() => navigationHandler.navigate('/pricing/lists')}>
          {t(`${COMPONENT}.get_started`)}
        </TextLink>
      </Text>

      <OnboardingChecklist>
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.created_list.label`)}
          description={t(`${COMPONENT}.checklist.created_list.description`)}
          action={{
            onClick: () => navigationHandler.navigate('/pricing/lists'),
            label: t(`${COMPONENT}.checklist.created_list.price_lists_link`),
          }}
          learnMoreLink={DOCS_URLS.managingPricing}
          checked={createdList}
          onChange={() => setCreatedList((prev) => !prev)}
        />
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.advanced.label`)}
          description={t(`${COMPONENT}.checklist.advanced.description`)}
          learnMoreLink={DOCS_URLS.quantityPricing}
          checked={reviewedAdvanced}
          onChange={() => setReviewedAdvanced((prev) => !prev)}
        />
      </OnboardingChecklist>

      <Text variant="secondary" size="sm">
        {t(`${COMPONENT}.troubleshooting_hint`)} <LearnMoreLink link={DOCS_URLS.troubleshooting} />
      </Text>

      <div>
        <Button
          variant={complete ? 'secondary' : 'primary'}
          disabled={!complete && !allChecked}
          loading={loading}
          onClick={onToggle}
        >
          {t(complete ? `${COMPONENT}.action_undo` : `${COMPONENT}.action`)}
        </Button>
      </div>
    </Accordion.Item>
  );
}
